import type {
  DiscoveryApi,
  ConfigApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import { DateTime, Duration } from 'luxon';

const DEFAULT_PROXY_PATH = '/prometheus/api';

/**
 * Prometheus API client compatible with the Roadie plugin's prometheusApiRef
 * (id: plugin.prometheus.service). Re-implemented locally to avoid importing
 * untyped internal dist paths from the Roadie package.
 *
 * Requests go through the Backstage proxy, which reaches Thanos Query Frontend
 * from the backend; the browser can't call it directly.
 *
 * @see https://github.com/RoadieHQ/roadie-backstage-plugins
 */
export class PrometheusApi {
  constructor(
    private readonly deps: {
      discoveryApi: DiscoveryApi;
      configApi: ConfigApi;
      fetchApi: FetchApi;
    },
  ) {}

  async getApiUrl() {
    const proxyUrl = await this.deps.discoveryApi.getBaseUrl('proxy');
    const proxyPath =
      this.deps.configApi.getOptionalString('prometheus.proxyPath') ??
      DEFAULT_PROXY_PATH;
    return `${proxyUrl}${proxyPath}`;
  }

  getUiUrl() {
    return this.deps.configApi.getOptionalString('prometheus.uiUrl');
  }

  async query({
    query = 'up',
    range = { hours: 1 },
    step = 14,
  }: {
    query?: string;
    range?: { hours?: number; minutes?: number };
    step?: number;
  }) {
    const apiUrl = await this.getApiUrl();
    const end = DateTime.now().toSeconds();
    const start = DateTime.now().minus(Duration.fromObject(range)).toSeconds();
    // PromQL contains characters that are significant in a query string, so it
    // has to be encoded rather than interpolated.
    const params = new URLSearchParams({
      query,
      start: String(start),
      end: String(end),
      step: String(step),
    });
    return this.fetch(`${apiUrl}/query_range?${params}`);
  }

  async getAlerts() {
    const apiUrl = await this.getApiUrl();
    return this.fetch(`${apiUrl}/rules?type=alert`);
  }

  private async fetch(url: string) {
    const response = await this.deps.fetchApi.fetch(url);
    if (!response.ok) {
      throw new Error(
        `Prometheus request failed: ${response.status} ${response.statusText}`,
      );
    }
    return response.json();
  }
}
